import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle, Clock, Package, Truck, AlertOctagon } from 'lucide-react';
import { DOMAIN_EVENTS } from '../../infrastructure/events/domainEventTypes';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCategoryLabel, getTitleTranslation, getMessageTranslation, getUITranslation } from '../../utils/notificationTranslations';
import { initializeNotificationService } from '../../services/notificationService';
import { getCurrentBusinessId } from '../../utils/sharedCore';
import {
  filterNotificationsForBusiness,
  loadPersistentNotificationsFromKv,
  mergeNotificationsRobust,
  resolveNotificationMatches,
  savePersistentNotificationsToKv,
  sanitizeNotificationList
} from '../../utils/notificationPersistence';
import '../../styles/components/notification-system.css';

let notificationIdSequence = 0;

function createNotificationId(prefix = 'notif') {
  notificationIdSequence += 1;
  return `${prefix}_${Date.now()}_${notificationIdSequence}_${Math.random().toString(36).slice(2, 10)}`;
}

function mergeNotificationsById(existing, incoming) {
  return mergeNotificationsRobust(existing, incoming);
}

function resolveActiveBusinessId() {
  try {
    return getCurrentBusinessId({ allowFallback: false }) || null;
  } catch {
    return null;
  }
}

// API (stable) vs state (toast/inbox lists) — POS toasts must not re-render the shell.
const NotificationApiContext = createContext(null);
const NotificationStateContext = createContext(null);

/** Stable writers: success/error/showNotification — safe in pages & keep-alive hosts. */
export const useNotification = () => {
  const context = useContext(NotificationApiContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

/** Toast + inbox lists — only Layout, NotificationCenter, toast stack, monitors. */
export const useNotificationState = () => {
  const context = useContext(NotificationStateContext);
  if (!context) {
    throw new Error('useNotificationState must be used within a NotificationProvider');
  }
  return context;
};

// Notification categories with priority system
export const NOTIFICATION_CATEGORIES = {
  EXPIRED: { 
    icon: AlertOctagon, 
    tone: 'danger',
    priority: 1,
    throttleMs: 0
  },
  EXPIRING: { 
    icon: Clock, 
    tone: 'warning',
    priority: 2,
    throttleMs: 5000
  },
  TRANSFER: { 
    icon: Package, 
    tone: 'info',
    priority: 3,
    throttleMs: 10000
  },
  REORDER: { 
    icon: AlertTriangle, 
    tone: 'success',
    priority: 4,
    throttleMs: 15000
  },
  DELIVERY: { 
    icon: Truck, 
    tone: 'accent',
    priority: 5,
    throttleMs: 20000
  },
  ECART: {
    icon: AlertTriangle,
    tone: 'warning',
    priority: 2,
    throttleMs: 0
  },
  GENERAL: { 
    icon: Info, 
    tone: 'muted',
    priority: 6,
    throttleMs: 30000
  }
};

const TOAST_TYPE_TONE = {
  success: 'success',
  error: 'danger',
  warning: 'warning',
  info: 'info'
};

const TOAST_EXIT_MS = 160;

/** Floating ephemeral toasts — body-portaled above receipt/confirm modals. Bell keeps persistent inbox. */
export const NotificationContainer = () => {
  const { dismissToast } = useNotification();
  const { notifications } = useNotificationState();
  const { currentLanguage } = useLanguage();

  if (!notifications.length || typeof document === 'undefined') return null;

  return createPortal(
    <div className="ns-toast-stack" role="region" aria-live="polite" aria-relevant="additions">
      {notifications.map((notification) => {
        const typeTone = TOAST_TYPE_TONE[notification.type] || 'info';
        const categoryTone =
          notification.category && NOTIFICATION_CATEGORIES[notification.category]
            ? NOTIFICATION_CATEGORIES[notification.category].tone
            : null;
        // Prefer type (success/error) over GENERAL→muted so sale/UI feedback stays clear
        const tone =
          notification.category && notification.category !== 'GENERAL' && categoryTone
            ? categoryTone
            : typeTone;
        const Icon =
          (notification.category && NOTIFICATION_CATEGORIES[notification.category]?.icon) ||
          (notification.type === 'success'
            ? CheckCircle
            : notification.type === 'error'
              ? AlertCircle
              : notification.type === 'warning'
                ? AlertTriangle
                : Info);

        return (
          <div
            key={notification.id}
            className={`ns-toast ns-toast--${tone}${notification.leaving ? ' ns-toast--leaving' : ''}`}
            role={notification.type === 'error' ? 'alert' : 'status'}
          >
            <div className="ns-toast__icon" aria-hidden>
              <Icon size={24} strokeWidth={2.6} />
            </div>
            <div className="ns-toast__body">
              <div className="ns-toast__title">{notification.title}</div>
              {notification.message ? (
                <div className="ns-toast__message">{notification.message}</div>
              ) : null}
            </div>
            <button
              type="button"
              className="ns-toast__close"
              onClick={() => dismissToast(notification.id)}
              aria-label={getUITranslation('deleteTooltip', currentLanguage) || 'Close'}
            >
              <X size={18} />
            </button>
          </div>
        );
      })}
    </div>,
    document.body
  );
};

// Provider des notifications
export const NotificationProvider = ({ children }) => {
  const { currentLanguage } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [persistentNotifications, setPersistentNotifications] = useState([]);
  const hydratedRef = useRef(false);
  const activeBusinessIdRef = useRef(resolveActiveBusinessId());
  const batchScheduledAtRef = useRef(0);
  const notificationsRef = useRef(notifications);
  const persistentNotificationsRef = useRef(persistentNotifications);
  notificationsRef.current = notifications;
  persistentNotificationsRef.current = persistentNotifications;

  useEffect(() => {
    initializeNotificationService(currentLanguage);
  }, [currentLanguage]);

  const cleanupOldNotifications = useCallback((list) => {
    return sanitizeNotificationList(list, {
      businessId: activeBusinessIdRef.current
    });
  }, []);

  const migrateNotificationTitles = useCallback((notificationsList) => {
    const englishToTranslation = {
      'Product Expired': getTitleTranslation('productExpired', currentLanguage),
      'Product Expiring Soon': getTitleTranslation('productExpiring', currentLanguage),
      'Reorder Needed': getTitleTranslation('reorderNeeded', currentLanguage),
      'Delivery Updates': getTitleTranslation('deliveryUpdate', currentLanguage),
      'System Error': getTitleTranslation('systemError', currentLanguage),
      'Expired Products': getCategoryLabel('EXPIRED', currentLanguage),
      'Expiring Soon': getCategoryLabel('EXPIRING', currentLanguage),
      'Transfer Requests': getCategoryLabel('TRANSFER', currentLanguage),
      'General': getCategoryLabel('GENERAL', currentLanguage)
    };

    return notificationsList.map((notification) => {
      if (englishToTranslation[notification.title]) {
        return {
          ...notification,
          title: englishToTranslation[notification.title]
        };
      }
      return notification;
    });
  }, [currentLanguage]);

  const hydrateForBusiness = useCallback((businessId) => {
    hydratedRef.current = false;
    activeBusinessIdRef.current = businessId || null;
    try {
      const { notifications: loaded } = loadPersistentNotificationsFromKv(businessId);
      const scoped = filterNotificationsForBusiness(loaded, businessId);
      const migrated = migrateNotificationTitles(scoped);
      setPersistentNotifications(migrated);
    } catch (error) {
      console.error('Error loading persistent notifications:', error);
      setPersistentNotifications([]);
    } finally {
      hydratedRef.current = true;
    }
  }, [migrateNotificationTitles]);

  useEffect(() => {
    hydrateForBusiness(resolveActiveBusinessId());
  }, [hydrateForBusiness]);

  useEffect(() => {
    const reloadIfBusinessChanged = () => {
      const nextId = resolveActiveBusinessId();
      if (String(nextId || '') === String(activeBusinessIdRef.current || '')) return;
      hydrateForBusiness(nextId);
    };

    const onDomainEvent = (event) => {
      const type = event?.detail?.type;
      if (
        type === 'business_switched' ||
        type === 'business_loaded' ||
        type === 'session_restored'
      ) {
        reloadIfBusinessChanged();
      }
    };

    window.addEventListener('storage', reloadIfBusinessChanged);
    window.addEventListener('elkheir:domain-event', onDomainEvent);
    const pollId = window.setInterval(reloadIfBusinessChanged, 15000);
    return () => {
      window.removeEventListener('storage', reloadIfBusinessChanged);
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
      window.clearInterval(pollId);
    };
  }, [hydrateForBusiness]);

  const saveToLocalStorage = useCallback(async (data) => {
    return new Promise((resolve, reject) => {
      try {
        const businessId = activeBusinessIdRef.current;
        const write = () => {
          try {
            savePersistentNotificationsToKv(businessId, data);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        if (window.requestIdleCallback) {
          window.requestIdleCallback(write);
        } else {
          idleSaveCallbackRef.current = write;
          queueMicrotask(() => {
            const run = idleSaveCallbackRef.current;
            idleSaveCallbackRef.current = null;
            run?.();
          });
        }
      } catch (error) {
        reject(error);
      }
    });
  }, []);

  useEffect(() => {
    if (!hydratedRef.current) return undefined;

    const saveNotifications = async () => {
      try {
        const cleanedNotifications = cleanupOldNotifications(persistentNotifications);
        if (cleanedNotifications.length !== persistentNotifications.length) {
          setPersistentNotifications(cleanedNotifications);
          return;
        }
        await saveToLocalStorage(cleanedNotifications);
      } catch (error) {
        console.error('Error saving persistent notifications:', error);
      }
    };

    saveNotifications();
  }, [persistentNotifications, cleanupOldNotifications, saveToLocalStorage]);

  const recoverFromError = useCallback((error, context) => {
    console.error('❌ [NOTIFICATIONS] Error in ' + context + ':', error);
    try {
      setNotificationBatch([]);
      notificationBatchRef.current = [];
      const errorTitle = getTitleTranslation('systemError', currentLanguage);
      const errorMessage = getMessageTranslation('systemError', currentLanguage);
      const errorNotification = {
        id: createNotificationId('error'),
        title: errorTitle,
        message: errorMessage,
        type: 'error',
        category: 'GENERAL',
        persistent: false,
        data: { error: error.message, context },
        read: false,
        createdAt: new Date().toISOString(),
        duration: 5000,
        priority: 1
      };
      setNotifications((prev) => mergeNotificationsById(prev, [errorNotification]));
    } catch (recoveryError) {
      console.error('❌ [NOTIFICATIONS] Recovery failed:', recoveryError);
    }
  }, [currentLanguage]);

  const throttledNotifications = useRef(new Map());
  const [notificationBatch, setNotificationBatch] = useState([]);
  const notificationBatchRef = useRef([]);
  const batchTimeoutRef = useRef(null);
  const removalTimersRef = useRef(new Set());
  const toastExitTimersRef = useRef(new Map());
  const idleSaveCallbackRef = useRef(null);

  const clearRemovalTimers = useCallback(() => {
    removalTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    removalTimersRef.current.clear();
    toastExitTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    toastExitTimersRef.current.clear();
  }, []);

  const scheduleRemoval = useCallback((callback, delay) => {
    const timerId = setTimeout(() => {
      removalTimersRef.current.delete(timerId);
      callback();
    }, delay);
    removalTimersRef.current.add(timerId);
    return timerId;
  }, []);

  // Hard remove (inbox + toast stack) — no exit animation
  const removeNotification = useCallback((id) => {
    try {
      const exitTimer = toastExitTimersRef.current.get(id);
      if (exitTimer) {
        clearTimeout(exitTimer);
        toastExitTimersRef.current.delete(id);
      }
      setNotifications(prev => {
        return prev.filter(notification => notification.id !== id);
      });
      setPersistentNotifications(prev => prev.filter(notification => notification.id !== id));
    } catch (error) {
      recoverFromError(error, 'removeNotification');
    }
  }, [recoverFromError]);

  /** Toast dismiss with leave animation, then hard remove. */
  const dismissToast = useCallback((id) => {
    try {
      if (toastExitTimersRef.current.has(id)) return;

      setNotifications((prev) => {
        const found = prev.find((n) => n.id === id);
        if (!found) return prev;
        if (found.leaving) return prev;
        return prev.map((n) => (n.id === id ? { ...n, leaving: true } : n));
      });

      const timerId = setTimeout(() => {
        toastExitTimersRef.current.delete(id);
        removeNotification(id);
      }, TOAST_EXIT_MS);
      toastExitTimersRef.current.set(id, timerId);
    } catch (error) {
      recoverFromError(error, 'dismissToast');
      removeNotification(id);
    }
  }, [recoverFromError, removeNotification]);

  // Throttling function to prevent notification spam
  const isThrottled = useCallback((category, key) => {
    const now = Date.now();
    const categoryInfo = NOTIFICATION_CATEGORIES[category];
    const throttleMs = categoryInfo?.throttleMs || 0;
    
    if (throttleMs === 0) return false; // No throttling for critical notifications
    
    const throttleKey = `${category}_${key}`;
    const lastSent = throttledNotifications.current.get(throttleKey);
    
    if (!lastSent || (now - lastSent) >= throttleMs) {
      throttledNotifications.current.set(throttleKey, now);
      return false;
    }
    
    return true;
  }, []);

  const processNotificationBatch = useCallback(() => {
    const batch = [...notificationBatchRef.current];
    notificationBatchRef.current = [];
    setNotificationBatch([]);

    if (batch.length === 0) return;

    const persistentBatch = batch.filter((n) => n.persistent);
    const nonPersistentBatch = batch.filter((n) => !n.persistent);

    if (nonPersistentBatch.length > 0) {
      setNotifications((prev) => mergeNotificationsById(prev, nonPersistentBatch));
    }

    if (persistentBatch.length > 0) {
      setPersistentNotifications((prev) => mergeNotificationsById(prev, persistentBatch));
    }

    batch.forEach((notification) => {
      const duration = notification.duration || 3000;
      if (notification.persistent) {
        scheduleRemoval(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
        }, duration);
      } else {
        scheduleRemoval(() => {
          dismissToast(notification.id);
        }, duration);
      }
    });
  }, [dismissToast, scheduleRemoval]);

  const showNotification = useCallback(({ title, message, type = 'info', duration = 3000, category, persistent = false, data = {} }) => {
    const businessId = activeBusinessIdRef.current || resolveActiveBusinessId();
    const scopedData = {
      ...(data && typeof data === 'object' ? data : {}),
      ...(businessId ? { businessId } : {})
    };

    // Throttle alert streams by eventKey (preferred) — never suppress ephemeral UI feedback
    const isUiFeedback = !persistent && (!category || category === 'GENERAL');
    const throttleKey =
      scopedData?.eventKey ||
      scopedData?.deviationId ||
      scopedData?.lotId ||
      scopedData?.productId ||
      title;
    if (!isUiFeedback && isThrottled(category, throttleKey)) {
      return null;
    }

    // Skip creating a duplicate persistent alert when the same eventKey is already in the inbox
    if (persistent && scopedData.eventKey) {
      const exists = persistentNotificationsRef.current.some(
        (n) => n?.data?.eventKey != null && String(n.data.eventKey) === String(scopedData.eventKey)
      );
      if (exists) return null;
    }

    const id = createNotificationId();
    const categoryInfo = NOTIFICATION_CATEGORIES[category];
    const priority = categoryInfo?.priority || 6;
    
    const newNotification = { 
      id, 
      title, 
      message, 
      type, 
      category, 
      persistent, 
      data: scopedData, 
      read: false,
      createdAt: new Date().toISOString(),
      duration,
      priority
    };

    setNotificationBatch((prev) => {
      const newBatch = [...prev, newNotification].sort((a, b) => a.priority - b.priority);
      notificationBatchRef.current = newBatch;
      return newBatch;
    });

    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
    }

    const batchDelay = priority <= 2 ? 10 : 50;
    batchScheduledAtRef.current = Date.now();
    batchTimeoutRef.current = setTimeout(processNotificationBatch, batchDelay);

    return id;
  }, [processNotificationBatch, isThrottled]);

  // Listen for deviation notification events + resolve-by-eventKey
  useEffect(() => {
    const handleDeviationNotification = (payload) => {
      const { title, message, type, category, persistent, data } = payload || {};
      const eventBiz =
        data?.businessId != null && String(data.businessId).trim()
          ? String(data.businessId)
          : null;
      const activeBiz = activeBusinessIdRef.current
        ? String(activeBusinessIdRef.current)
        : null;
      // Drop écart alerts meant for another business on a shared PC
      if (eventBiz && activeBiz && eventBiz !== activeBiz) {
        return;
      }
      showNotification({
        title,
        message,
        type,
        category: category || 'ECART',
        persistent,
        data
      });
    };

    const handleResolveNotification = (payload) => {
      const match = payload || {};
      setPersistentNotifications((prev) =>
        prev.filter((n) => !resolveNotificationMatches(n, match))
      );
      setNotifications((prev) => prev.filter((n) => !resolveNotificationMatches(n, match)));
    };

    const onDomainEvent = (event) => {
      if (event?.detail?.type === 'deviation_notification') {
        handleDeviationNotification(event.detail.payload);
      }
      if (
        event?.detail?.type === 'notification_resolve' ||
        event?.detail?.type === DOMAIN_EVENTS?.NOTIFICATION_RESOLVE
      ) {
        handleResolveNotification(event.detail.payload || event.detail);
      }
    };

    window.addEventListener('elkheir:domain-event', onDomainEvent);

    return () => {
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
    };
  }, [showNotification]);

  // Cleanup batch and removal timers on unmount
  useEffect(() => {
    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      clearRemovalTimers();
      idleSaveCallbackRef.current = null;
    };
  }, [clearRemovalTimers]);

  const markAsRead = useCallback((id) => {
    setPersistentNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, []);

  // Cleanup old throttling data
  const cleanupThrottlingData = useCallback(() => {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    for (const [key, timestamp] of throttledNotifications.current.entries()) {
      if (now - timestamp > maxAge) {
        throttledNotifications.current.delete(key);
      }
    }
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setPersistentNotifications([]);
  }, []);

  // Enhanced recoverFromError with full context
  const enhancedRecoverFromError = useCallback((error, context) => {
    console.error(`❌ [NOTIFICATIONS] Error in ${context}:`, error);
    
    try {
      // Clear potentially corrupted state
      setNotificationBatch([]);
      notificationBatchRef.current = [];
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }
      
      // Reset throttling data if it's corrupted
      if (error.message?.includes('throttling')) {
        throttledNotifications.current.clear();
      }
      
      // Get translations for error notification
      const errorTitle = getTitleTranslation('systemError', currentLanguage);
      const errorMessage = getMessageTranslation('systemError', currentLanguage);
      
      // Show error notification to user
      const errorNotification = {
        id: createNotificationId('error'),
        title: errorTitle,
        message: errorMessage,
        type: 'error',
        category: 'GENERAL',
        persistent: false,
        data: { error: error.message, context },
        read: false,
        createdAt: new Date().toISOString(),
        duration: 5000,
        priority: 1
      };
      
      setNotifications((prev) => mergeNotificationsById(prev, [errorNotification]));
      
    } catch (recoveryError) {
      console.error('❌ [NOTIFICATIONS] Recovery failed:', recoveryError);
    }
  }, [currentLanguage]);

  // Periodic cleanup and maintenance
  useEffect(() => {
    const maintenanceInterval = setInterval(() => {
      try {
        cleanupThrottlingData();
        // Clean up stale batch timeouts using schedule timestamp (not timer id)
        if (
          batchTimeoutRef.current &&
          batchScheduledAtRef.current &&
          Date.now() - batchScheduledAtRef.current > 60000
        ) {
          clearTimeout(batchTimeoutRef.current);
          batchTimeoutRef.current = null;
          batchScheduledAtRef.current = 0;
        }
      } catch (error) {
        enhancedRecoverFromError(error, 'maintenance');
      }
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(maintenanceInterval);
  }, [cleanupThrottlingData, enhancedRecoverFromError]);

  const getUnreadCount = useCallback(() => {
    return persistentNotificationsRef.current.filter((n) => !n.read).length;
  }, []);

  const resolveNotification = useCallback((idOrMatch) => {
    if (idOrMatch && typeof idOrMatch === 'object') {
      setPersistentNotifications((prev) =>
        prev.filter((n) => !resolveNotificationMatches(n, idOrMatch))
      );
      setNotifications((prev) =>
        prev.filter((n) => !resolveNotificationMatches(n, idOrMatch))
      );
      return;
    }
    removeNotification(idOrMatch);
  }, [removeNotification]);

  // Méthodes utilitaires pour différents types de notifications
  const success = useCallback((title, message = '', options = {}) => {
    // Support both old format (category string) and new format (options object)
    if (typeof options === 'string') {
      // Old format: success(title, message, category)
      return showNotification({ title, message, type: 'success', category: options, duration: 4500 });
    }
    
    // New format: success(title, message, { category, duration, onUndo, ... })
    const { category = 'GENERAL', duration = 4500, onUndo, ...restOptions } = options;
    return showNotification({ 
      title, 
      message, 
      type: 'success', 
      category,
      duration,
      data: { onUndo, ...restOptions },
      ...restOptions
    });
  }, [showNotification]);

  const error = useCallback((title, message = '', category = 'GENERAL') => {
    return showNotification({ title, message, type: 'error', category, duration: 6500 });
  }, [showNotification]);

  const warning = useCallback((title, message = '', options = {}) => {
    if (typeof options === 'string') {
      return showNotification({ title, message, type: 'warning', category: options, duration: 5500 });
    }
    const { category = 'GENERAL', duration = 5500, ...restOptions } = options;
    return showNotification({
      title,
      message,
      type: 'warning',
      category,
      duration,
      ...restOptions
    });
  }, [showNotification]);

  const info = useCallback((title, message = '', category = 'GENERAL') => {
    return showNotification({ title, message, type: 'info', category, duration: 4500 });
  }, [showNotification]);

  const persistent = useCallback((title, message = '', category = 'GENERAL', data = {}, type = 'info') => {
    return showNotification({
      title,
      message,
      type: type || 'info',
      category,
      persistent: true,
      data
    });
  }, [showNotification]);

  const apiValue = useMemo(
    () => ({
      showNotification,
      removeNotification,
      dismissToast,
      markAsRead,
      clearAllNotifications,
      getUnreadCount,
      resolveNotification,
      success,
      error,
      warning,
      info,
      persistent,
      recoverFromError: enhancedRecoverFromError
    }),
    [
      showNotification,
      removeNotification,
      dismissToast,
      markAsRead,
      clearAllNotifications,
      getUnreadCount,
      resolveNotification,
      success,
      error,
      warning,
      info,
      persistent,
      enhancedRecoverFromError
    ]
  );

  // Kassierer Willkommen / Abschied / Pause (RoleContext emitCashierGreeting)
  useEffect(() => {
    const onGreeting = (event) => {
      const { type, workerName } = event?.detail || {};
      if (!type) return;
      const name = workerName || '';
      const lang = currentLanguage === 'ar' ? 'ar' : 'fr';
      const pick = (fr, ar) => (lang === 'ar' ? ar : fr);
      if (type === 'welcome') {
        success(
          pick('Bienvenue', 'مرحباً'),
          pick(
            `Bienvenue, ${name} ! Bonne journée de caisse.`,
            `مرحباً، ${name}! يوم طيب في الصندوق.`
          ),
          { duration: 5000 }
        );
      } else if (type === 'resume') {
        success(
          pick('Reprise', 'استئناف'),
          pick(
            `Reprise réussie, ${name}. Bonne continuation !`,
            `تمت الاستعادة، ${name}. واصل بنجاح!`
          ),
          { duration: 4500 }
        );
      } else if (type === 'farewell') {
        success(
          pick('Bonne fin de service', 'نهاية طيبة'),
          pick(
            `Bonne fin de service, ${name} ! Merci pour votre travail. À bientôt.`,
            `نهاية طيبة، ${name}! شكراً على عملك. إلى اللقاء.`
          ),
          { duration: 5500 }
        );
      } else if (type === 'pause') {
        info(
          pick('Pause de caisse', 'إيقاف مؤقت للصندوق'),
          pick(
            `${name}, caisse en pause — seul vous pourrez reprendre. Aucun autre caissier ne peut se connecter.`,
            `${name}، الصندوق متوقف — أنت وحدك من يمكنه الاستئناف. لا يمكن لأي كاشير آخر الدخول.`
          )
        );
      }
    };
    window.addEventListener('aman:cashier-greeting', onGreeting);
    return () => window.removeEventListener('aman:cashier-greeting', onGreeting);
  }, [success, info, currentLanguage]);

  const stateValue = useMemo(
    () => ({
      notifications,
      persistentNotifications
    }),
    [notifications, persistentNotifications]
  );

  return (
    <NotificationApiContext.Provider value={apiValue}>
      <NotificationStateContext.Provider value={stateValue}>
        {children}
        <NotificationContainer />
      </NotificationStateContext.Provider>
    </NotificationApiContext.Provider>
  );
};
